/**
 * THE KEYS, IN THE OFFICE'S WORDS (ops #100): the pure half of the Keys card on a home's page and of
 * the keys a cleaner holds on the crew list.
 *
 * Nothing here decides anything the server does not decide again: which keys a movement may pick is
 * what the form offers, so nobody is offered a choice the server refuses, and the server holds every
 * rule (`api/src/records/keys.ts`). A kind is worded as the cleaner's sheet words it (`humanise`), and
 * an event in the office's words, the household and never the client.
 */
import {
  KEY_CUSTODY_EVENTS,
  KEY_KINDS,
  humanise,
  type KeyCustodyEvent,
  type KeyKind,
} from '@fk/shared';
import type { ApiError, KeyMovement, KeyNow } from './api.ts';
import { dayOf, formatInstant, mediumDay, timeOfDayOf } from './format.ts';

/** "House key", "Gate fob": the words the cleaner's sheet already draws a kind in. */
export const KEY_KIND_WORDS: Readonly<Record<KeyKind, string>> = Object.freeze(
  Object.fromEntries(KEY_KINDS.map((kind) => [kind, humanise(kind)])) as Record<KeyKind, string>,
);

/** What happened to a key, as the form offers it. */
export const KEY_EVENT_LABELS: Readonly<Record<KeyCustodyEvent, string>> = Object.freeze({
  received_from_client: 'Received from the household',
  handed_to_cleaner: 'Handed to a cleaner',
  returned_by_cleaner: 'Returned by a cleaner',
  returned_to_client: 'Given back to the household',
  lost: 'Lost',
  destroyed: 'Destroyed',
});

/** Every event, in the vocabulary's order, for the form's select. */
export const KEY_EVENTS: readonly KeyCustodyEvent[] = KEY_CUSTODY_EVENTS;

/** The events a household's person may be named on. */
export const NAMES_A_PERSON: readonly KeyCustodyEvent[] = [
  'received_from_client',
  'returned_to_client',
];

/** The events that name the cleaner, a hand and a return. */
export const NAMES_A_CLEANER: readonly KeyCustodyEvent[] = [
  'handed_to_cleaner',
  'returned_by_cleaner',
];

/** "House key, brass key, blue tag". */
export const keyName = (key: { keyKind: KeyKind; keyLabel: string }): string =>
  `${KEY_KIND_WORDS[key.keyKind]}, ${key.keyLabel}`;

const dayWords = (instant: string): string => {
  const day = dayOf(instant);
  return day ? mediumDay(day) : instant;
};

/** The newest movement of one key in a chain read newest first, or null. */
function latestOf(key: KeyNow, chain: readonly KeyMovement[]): KeyMovement | null {
  return (
    chain.find((move) => move.keyKind === key.keyKind && move.keyLabel === key.keyLabel) ?? null
  );
}

/**
 * Where a key is now: "At the office since Sep 1, 2026", "With Maria since Sep 3, 2026", or for a key
 * the company no longer holds what ended it, read from the chain, "Lost on Sep 5, 2026".
 */
export function keyNowWords(key: KeyNow, chain: readonly KeyMovement[]): string {
  const since = dayWords(key.since);
  if (key.at === 'office') return `At the office since ${since}`;
  if (key.at === 'cleaner') return `With ${key.cleaner?.displayName ?? 'a cleaner'} since ${since}`;
  const last = latestOf(key, chain);
  if (
    last &&
    (last.event === 'returned_to_client' || last.event === 'lost' || last.event === 'destroyed')
  ) {
    return `${KEY_EVENT_LABELS[last.event]} on ${since}`;
  }
  return `No longer held, since ${since}`;
}

/** The household's people, by id, as the home's page has them. */
export interface PersonOnFile {
  readonly id: string;
  readonly displayName: string;
}

const lowerFirst = (words: string): string => words.charAt(0).toLowerCase() + words.slice(1);

/**
 * One movement of the chain as two lines: what happened to which key and who, and when it happened
 * and who recorded it. A person no longer on file is not named.
 */
export function movementWords(
  move: KeyMovement,
  people: readonly PersonOnFile[],
): { what: string; when: string } {
  const person = move.contactId
    ? (people.find((one) => one.id === move.contactId)?.displayName ?? null)
    : null;
  const cleaner = move.cleaner?.displayName ?? null;
  let happened: string;
  switch (move.event) {
    case 'received_from_client':
      happened = `received from the household${person ? `, from ${person}` : ''}`;
      break;
    case 'returned_to_client':
      happened = `given back to the household${person ? `, to ${person}` : ''}`;
      break;
    case 'handed_to_cleaner':
      happened = `handed to ${cleaner ?? 'a cleaner'}`;
      break;
    case 'returned_by_cleaner':
      happened = `returned by ${cleaner ?? 'a cleaner'}`;
      break;
    case 'lost':
    case 'destroyed':
      happened = `${lowerFirst(KEY_EVENT_LABELS[move.event])} ${cleaner ? `while ${cleaner} held it` : 'at the office'}`;
      break;
  }
  return {
    what: `${keyName(move)}: ${happened}`,
    when: `${formatInstant(move.occurredAt) ?? move.occurredAt}. Recorded by ${move.recordedBy.displayName}.`,
  };
}

/**
 * The keys a movement may pick: none for a receipt, which types its key; the keys at the office for a
 * hand or a return to the household; a cleaner's keys for their return; every key the company holds
 * for a loss. A key already gone is never offered, so a typo never makes a phantom key.
 */
export function keysFor(event: KeyCustodyEvent, keys: readonly KeyNow[]): KeyNow[] {
  switch (event) {
    case 'received_from_client':
      return [];
    case 'handed_to_cleaner':
    case 'returned_to_client':
      return keys.filter((key) => key.at === 'office');
    case 'returned_by_cleaner':
      return keys.filter((key) => key.at === 'cleaner');
    case 'lost':
    case 'destroyed':
      return keys.filter((key) => key.at !== 'gone');
  }
}

/** What the form says when a movement has no key to pick. */
export function noKeyWords(event: KeyCustodyEvent): string {
  switch (event) {
    case 'returned_by_cleaner':
      return 'No cleaner holds a key of this home now.';
    case 'lost':
    case 'destroyed':
      return 'The company holds no key of this home now.';
    default:
      return 'No key of this home is at the office now.';
  }
}

/** "House key, brass key, blue tag, at the office", "Gate fob, grey fob, with Maria". */
export function keyChoiceWords(key: KeyNow): string {
  return `${keyName(key)}, ${key.at === 'cleaner' ? `with ${key.cleaner?.displayName ?? 'a cleaner'}` : 'at the office'}`;
}

/** The Florida day and clock of an instant, the form's "now", to the minute. */
export function floridaNow(now: Date): { day: string; time: string } {
  const instant = now.toISOString();
  return { day: dayOf(instant) ?? '', time: timeOfDayOf(instant) ?? '' };
}

/** A field the server names, by its name on the form. */
const FIELD_WORDS: Readonly<Record<string, string>> = Object.freeze({
  keyKind: 'The kind of key',
  keyLabel: "The key's name",
  event: 'What happened',
  cleanerId: 'The cleaner',
  contactId: 'The person named',
  occurredAt: 'When it happened',
  note: 'The note',
});

/** A UTC instant as the server writes one into a sentence. */
const INSTANT = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z/g;

/**
 * A refusal in plain office words. The server's message is already a sentence and is preferred; a
 * field it names by its name in the code is said by its name on the form, and an instant in it is said
 * as a Florida time.
 */
export function keyRefusalWords(error: ApiError): string {
  let words = error.message;
  const colon = words.indexOf(': ');
  if (colon > 0) {
    const field = FIELD_WORDS[words.slice(0, colon)];
    if (field) words = `${field} ${words.slice(colon + 2)}`;
  }
  return words.replace(INSTANT, (instant) => formatInstant(instant) ?? instant);
}
